import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useErrorToast } from "@/lib/hooks";
import { useAuth } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

export const KINDS = ["site", "room", "zone", "shelf", "box", "other"];

export const LocationDialog = ({ open, onOpenChange, parentId = null, initial = null, onSaved }) => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const showErr = useErrorToast();
  const [name, setName] = useState("");
  const [kind, setKind] = useState(parentId ? "box" : "site");
  useEffect(() => {
    if (open) { setName(initial?.name || ""); setKind(initial?.kind || (parentId ? "box" : "site")); }
  }, [open, initial, parentId]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { name, kind, parent_id: parentId };
      const { data } = initial ? await api.put(`/locations/${initial.id}`, body) : await api.post("/locations", body);
      qc.invalidateQueries({ queryKey: ["locations"] });
      qc.invalidateQueries({ queryKey: ["location"] });
      if (!initial) refresh();
      onOpenChange(false);
      onSaved?.(data);
    } catch (err) { showErr(err); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="location-dialog">
        <form onSubmit={save} className="space-y-5">
          <DialogHeader><DialogTitle>{initial ? t("common.edit") : parentId ? t("loc.addChild") : t("loc.addPlace")}</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>{t("common.name")}</Label>
            <Input data-testid="location-name-input" className="h-12" value={name} onChange={(e) => setName(e.target.value)} required autoFocus /></div>
          <div className="space-y-1.5"><Label>{t("loc.kind")}</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="location-kind-select" className="h-12"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{t(`kind.${k}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button data-testid="location-save-button" type="submit" className="h-11">{t("common.save")}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
